import CustomerAvatar from "features/customer/components/CustomerAvatar";
import CustomerSimplifiedDTO from "features/customer/dtos/CustomerSimplifiedDTO";
import Card from "features/shared/components/card/Card";
import IDataFilterService from "features/shared/components/dataFilter/IDataFilterService";
import { DataGridId } from "features/shared/components/dataGrid/DataGridId";
import CommodityIcon from "features/shared/components/icons/CommodityIcon";
import { Dashboard_CustomerCard } from "features/shared/components/loaders/promiseLoadingAreas";
import PromiseLoadingSpinner from "features/shared/components/loaders/PromiseLoadingSpinner";
import { loadPortalConfig } from "features/shared/helpers/modules";
import { getContractsRoute } from "features/shared/helpers/routeHelper";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import React, { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ContractCountDTO from "../contracts/dtos/ContractCountDTO";
import styles from "./customercard.module.scss";
import IUrlParam from "features/shared/components/dataFilter/IUrlParam";
import { BoolEnum } from "features/shared/enums/boolEnum";
import { getCommodityColorSuffix } from "features/contracts/helpers/commodityDetailHelper";


const portalConfig = loadPortalConfig();

interface IProps {
  customer: CustomerSimplifiedDTO;
  contractCounts: ContractCountDTO[];
  headerBg?: string;
}

const CustomerCard: React.FC<IProps> = (props) => {
  // hooks
  const { t } = useTranslation("contracts\\enums\\commodityDetail");
  const tLabels = useTranslation("dashboard\\dashboard").t;

  // props
  const { customer, contractCounts, headerBg } = props;
  
  // DI
  const dataFilterService = useInjection<IDataFilterService>(IocTypes.DataFilterService);
  
  // HELPERS
  const getHeaderBackgorund = (): CSSProperties | undefined => {
    if (headerBg) {
      return {
        background: headerBg,
      };
    }
  };

  const getLinkItemClass = () => {
    let padding = contractCounts.length < 3 ? " py-3" : "";   
    return `${styles.contractItem}${padding}`; 
  }

  const getContractsTranslate = (contractCount: ContractCountDTO): string => {
    let contractsPrefix = tLabels("customerCard.contractsPrefix");
    let commodityPart = t(contractCount.commodityDetail!);
    let contractsPart = contractsPrefix && commodityPart ? commodityPart.charAt(0).toLocaleLowerCase() + commodityPart.slice(1) : commodityPart;
    return `${contractsPrefix ? contractsPrefix + " " : ""}${contractsPart}`;
  };

  const storeContractsFilterParams = (contractCount: ContractCountDTO) => {
    let dataGridId = portalConfig.useContractualAccounts
      ? DataGridId.contractualAccountsGrid
      : DataGridId.contractGrid;

    let params: IUrlParam[] = [];

    if (contractCount.commodityDetail) {
      params.push({ key: "CommodityDetail", value: contractCount.commodityDetail });
    }
    if (contractCount.isProduction) {
      params.push({ key: "IsProduction", value: BoolEnum.True });
    }

    dataFilterService.storeSetParams(dataGridId, params);
  };

  // sub-renders
  const renderContractLinks = () => {
    return contractCounts.map((item, index) => renderContractLink(item, index))
  };
  
  const renderContractLink = (contractCount: ContractCountDTO, index: number) => {
    let commodityColorSuffix = getCommodityColorSuffix(contractCount.commodityDetail!, contractCount.isProduction)

    return (
      <Link
        to={getContractsRoute()}
        onClick={() => storeContractsFilterParams(contractCount)}
        className={`list-group-item list-group-item-action ${getLinkItemClass()}`}
        key={index}
      >
        <div className={`d-flex align-items-center text-${commodityColorSuffix}`}>
          <span className={styles.contractItemIcon}>
            <CommodityIcon commodityDetail={contractCount.commodityDetail!} />
          </span>
          <span className="ml-2">
            {getContractsTranslate(contractCount)}
            {contractCount.isProduction ? ` - ${tLabels("customerCard.productionLabel")}` : ""}
          </span>
        </div>
        <span className={`badge badge-${commodityColorSuffix} badge-pill text-white`}>
          {contractCount.count}
        </span>
      </Link>
    );
  };
  
  // render
  return (
    <Card className={styles.customerCard}>
      <div className={styles.customerCardHeader} style={getHeaderBackgorund()}>
        <h3 className={styles.customerCardTitle}>
          <Link to="/customer" className={styles.customerCardTitleLink}>
            {customer.name}
          </Link>
        </h3>
        <h4 className={styles.customerCardSubtitle}>
          {tLabels("customerCard.customerNumber")} {customer.customerNumber}
        </h4>
        <div className={styles.customerCardAvatar}>
          <CustomerAvatar
            customerType={customer.customerType!}
            size={85}
            iconSize={2}
          />
        </div>
      </div>
      <div
        className={`list-group list-group-flush position-relative ${styles.contractsList}`}
      >
        {renderContractLinks()}
        <PromiseLoadingSpinner area={Dashboard_CustomerCard} />
      </div>
    </Card>
  );
};

export default CustomerCard;
